import { useCallback, useEffect, useState } from 'react';

import { applyTheme } from '.';
import { DEFAULT_THEME, STORAGE_KEY, THEME_CONFIG, THEME_QUERY_PARAM } from './config';
import { getTheme, THEMES } from './themes';
import { DynamicTheme, ThemeName } from './types';
import { updateFavicon } from './utils';

/**
 * Obtiene el nombre del tema desde los query params de la URL
 * Ejemplo: https://app.fielpet.com?theme=GALICIA
 */
const getThemeFromUrl = (): ThemeName | null => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get(THEME_QUERY_PARAM)?.toUpperCase();

    if (themeParam && themeParam in THEMES) {
      return themeParam as ThemeName;
    }
  } catch (error) {
    void error;
  }
  return null;
};

/**
 * Obtiene el tema almacenado en localStorage
 */
const getStoredTheme = (): DynamicTheme | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: DynamicTheme = JSON.parse(stored);
      if (parsed?.colors && parsed?.themeName) {
        return parsed;
      }
    }
  } catch (error) {
    void error;
  }
  return null;
};

/**
 * Resuelve el tema con la siguiente prioridad:
 * 1. Query param en URL (?theme=GALICIA) - Solo si dynamicThemeEnabled
 * 2. Tema almacenado en localStorage - Solo si dynamicThemeEnabled
 * 3. Tema fijo (config) o por defecto (FIELPET)
 */
const resolveTheme = (): { theme: DynamicTheme; source: 'url' | 'storage' | 'default' | 'fixed' } => {
  // Si el tema dinámico está deshabilitado, usar tema fijo
  if (!THEME_CONFIG.dynamicThemeEnabled) {
    return { theme: getTheme(THEME_CONFIG.fixedTheme), source: 'fixed' };
  }

  // 1️⃣ Prioridad: URL query param
  const urlThemeName = getThemeFromUrl();
  if (urlThemeName) {
    return { theme: getTheme(urlThemeName), source: 'url' };
  }

  // 2️⃣ Segundo: localStorage
  const storedTheme = getStoredTheme();
  if (storedTheme) {
    return { theme: storedTheme, source: 'storage' };
  }

  // 3️⃣ Fallback: tema por defecto
  return { theme: getTheme(DEFAULT_THEME), source: 'default' };
};

/**
 * Limpia el query param del tema de la URL sin recargar la página
 */
const cleanThemeFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(THEME_QUERY_PARAM)) {
      url.searchParams.delete(THEME_QUERY_PARAM);
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
    void error;
  }
};

export const useDynamicTheme = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [theme, setTheme] = useState<DynamicTheme | null>(null);

  useEffect(() => {
    try {
      const { theme: currentTheme, source } = resolveTheme();

      applyTheme(currentTheme.colors);
      setTheme(currentTheme);

      // Si viene de URL, guardamos en localStorage y limpiamos la URL
      if (source === 'url') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(currentTheme));
        cleanThemeFromUrl();
      }

      // Actualizar título y favicon dinámicamente
      if (currentTheme?.brandName) {
        document.title = currentTheme.brandName;
      }

      if (currentTheme?.logoUrl) {
        updateFavicon(currentTheme.brandName, currentTheme.logoUrl);
      }
    } catch (error) {
      void error;
      const fallbackTheme = getTheme(DEFAULT_THEME);
      applyTheme(fallbackTheme.colors);
      setTheme(fallbackTheme);
    } finally {
      setLoading(false);
    }
  }, []);

  const setDynamicTheme = useCallback((newTheme: DynamicTheme) => {
    // No-op si el tema dinámico está deshabilitado
    if (!THEME_CONFIG.dynamicThemeEnabled) return;

    if (!newTheme?.colors || !newTheme?.themeName) {
      return;
    }

    try {
      applyTheme(newTheme.colors);
      setTheme(newTheme);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newTheme));
      document.title = newTheme.brandName;
      updateFavicon(newTheme.brandName, newTheme.logoUrl);
    } catch (error) {
      void error;
    }
  }, []);

  const clearTheme = useCallback(() => {
    // No-op si el tema dinámico está deshabilitado
    if (!THEME_CONFIG.dynamicThemeEnabled) return;

    localStorage.removeItem(STORAGE_KEY);
    const defaultTheme = getTheme(DEFAULT_THEME);
    applyTheme(defaultTheme.colors);
    setTheme(defaultTheme);
  }, []);

  return { loading, theme, setDynamicTheme, clearTheme };
};
